import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { StrengthBadge } from "../components/Badges";
import { CaseCard } from "../components/CaseCard";
import { CaseDetailDialog } from "../components/CaseDetailDialog";
import { CaseSimilarityChart, ActualResultsChart } from "../components/Charts";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, HelpCircle, TrendingUp, TrendingDown,
  Lightbulb, GitCompareArrows, Loader2, ChevronRight, Info, RefreshCw, CheckCheck,
  Download, ExternalLink, BarChart3,
} from "lucide-react";

const STAGES = [
  "Understanding your decision",
  "Finding comparable businesses",
  "Evaluating similarity",
  "Collecting evidence",
  "Analyzing outcomes",
  "Comparing with your business",
  "Building decision brief",
];

function Analyzing({ decisionText }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => Math.min(a + 1, STAGES.length - 1)), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="max-w-2xl mx-auto pt-6">
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
          </span>
          <h2 className="font-display text-xl font-semibold text-slate-900">Researching comparable decisions</h2>
        </div>
        <p className="text-sm text-slate-500 mb-8 line-clamp-2">"{decisionText}"</p>
        <div className="space-y-4">
          {STAGES.map((s, i) => {
            const done = i < active, current = i === active;
            return (
              <motion.div key={s} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-500" : current ? "bg-blue-500" : "bg-slate-100"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4 text-white" /> : current ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                </div>
                <span className={`text-sm ${done || current ? "text-slate-900 font-medium" : "text-slate-400"}`}>{s}</span>
              </motion.div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-8 font-mono">This can take up to a minute. You can leave and come back.</p>
      </div>
    </div>
  );
}

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`bg-white border border-slate-200 rounded-xl shadow-sm ${className}`}>{children}</div>;
}

function SummaryGrid({ s }) {
  const rows = [
    ["Decision type", s.decision_type], ["Current state", s.current_state],
    ["Proposed change", s.proposed_change], ["Magnitude", s.magnitude],
    ["Product / service", s.product_service], ["Customer segment", s.customer_segment],
    ["Geographic scope", s.geographic_scope], ["Objective", s.objective],
    ["Reason", s.reason], ["Timing", s.timing], ["Constraints", s.constraints],
  ].filter(([, v]) => v);
  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {rows.map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-slate-400">{k}</span>
          <span className="text-sm text-slate-800">{v}</span>
        </div>
      ))}
    </div>
  );
}

function OutcomeBar({ b }) {
  const total = (b.positive || 0) + (b.negative || 0) + (b.mixed || 0) + (b.unclear || 0) || 1;
  const seg = [
    { k: "positive", v: b.positive, c: "bg-emerald-500", label: "positive" },
    { k: "negative", v: b.negative, c: "bg-rose-500", label: "negative" },
    { k: "mixed", v: b.mixed, c: "bg-amber-500", label: "mixed" },
    { k: "unclear", v: b.unclear, c: "bg-slate-300", label: "unclear" },
  ];
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
        {seg.map((s) => s.v > 0 && <div key={s.k} className={s.c} style={{ width: `${(s.v / total) * 100}%` }} />)}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3">
        {seg.map((s) => (
          <span key={s.k} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className={`w-2.5 h-2.5 rounded-sm ${s.c}`} /> <span className="font-mono font-semibold text-slate-900">{s.v || 0}</span> {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function List({ items, icon: Icon, tone }) {
  const toneCls = { pos: "text-emerald-600", neg: "text-rose-600", warn: "text-amber-600", info: "text-slate-400" }[tone] || "text-slate-400";
  if (!items?.length) return <p className="text-sm text-slate-400">None identified.</p>;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
          <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${toneCls}`} /> {it}
        </li>
      ))}
    </ul>
  );
}

function FollowUp({ questions, onSubmit }) {
  const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false);
  return (
    <Card className="p-6 border-blue-200 bg-blue-50/40" testid="follow-up-card">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="w-5 h-5 text-blue-600" />
        <h3 className="font-display text-lg font-semibold text-slate-900">A few useful details would sharpen this analysis</h3>
      </div>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={i}>
            <Label className="text-sm text-slate-700">{q}</Label>
            <Input data-testid={`followup-input-${i}`} className="mt-1.5 rounded-lg bg-white"
              value={ans[q] || ""} onChange={(e) => setAns({ ...ans, [q]: e.target.value })} />
          </div>
        ))}
      </div>
      <Button data-testid="followup-submit" disabled={busy} className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-lg gap-2"
        onClick={async () => { setBusy(true); await onSubmit(ans); setBusy(false); }}>
        <RefreshCw className="w-4 h-4" /> Re-run with these answers
      </Button>
    </Card>
  );
}

function ImplementBlock({ decision, analysis, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ revenue_change: "", sales_change: "", conversion_change: "", customer_response: "", cost_change: "", profit_change: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const fields = [
    ["revenue_change", "Revenue change"], ["sales_change", "Sales change"],
    ["conversion_change", "Conversion change"], ["customer_response", "Customer response"],
    ["cost_change", "Cost change"], ["profit_change", "Profit change"],
  ];
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/decisions/${decision.id}/implement`, form);
      onSaved(data); toast.success("Recorded your actual results");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (decision.implemented && decision.actual_results) {
    const ar = decision.actual_results;
    const ob = analysis?.outcome_breakdown || {};
    const totalKnown = (ob.positive || 0) + (ob.negative || 0);
    return (
      <Card className="p-6" testid="actual-results-card">
        <div className="flex items-center gap-2 mb-4">
          <CheckCheck className="w-5 h-5 text-emerald-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Your actual results vs. the analogs</h3>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className="grid grid-cols-2 gap-4">
              {fields.map(([k, l]) => ar[k] && (
                <div key={k}>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">{l}</span>
                  <p className="text-sm text-slate-800 font-medium mt-0.5">{ar[k]}</p>
                </div>
              ))}
            </div>
            {ar.notes && <p className="text-sm text-slate-600 mt-4 pt-4 border-t border-slate-100">{ar.notes}</p>}
          </div>
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Your numeric changes</span>
            <ActualResultsChart actual={ar} />
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-slate-100 flex items-start gap-3">
          <BarChart3 className="w-4.5 h-4.5 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Among the comparable cases, <span className="font-semibold text-emerald-700">{ob.positive || 0} were positive</span> and{" "}
            <span className="font-semibold text-rose-700">{ob.negative || 0} were negative</span>
            {totalKnown > 0 && <> — roughly <span className="font-mono font-semibold">{Math.round((ob.positive / totalKnown) * 100)}%</span> of the clear-outcome cases went the intended way</>}.
            Read your own numbers above against that base rate rather than any single case.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6" testid="implement-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Already made this decision?</h3>
          <p className="text-sm text-slate-500 mt-1">Record what actually happened to build your own learning loop.</p>
        </div>
        {!open && <Button variant="outline" className="rounded-lg" data-testid="mark-implemented-button" onClick={() => setOpen(true)}>Mark implemented</Button>}
      </div>
      {open && (
        <div className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map(([k, l]) => (
              <div key={k}>
                <Label className="text-sm text-slate-700">{l}</Label>
                <Input data-testid={`actual-${k}`} className="mt-1.5 rounded-lg" value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="e.g. +8%" />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-sm text-slate-700">Notes</Label>
            <Textarea className="mt-1.5 rounded-lg" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <Button disabled={busy} onClick={save} data-testid="save-actual-button" className="bg-slate-900 hover:bg-slate-800 rounded-lg">
            {busy ? "Saving…" : "Save results"}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default function DecisionReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openCase, setOpenCase] = useState(null);
  const pollRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    try {
      const { data } = await api.get(`/decisions/${id}`);
      setDecision(data);
      return data;
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else console.error("Failed to load decision", e);
      return null;
    }
  }, [id]);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const d = await fetchOnce();
      if (stop) return;
      if (d && d.status === "analyzing") pollRef.current = setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; clearTimeout(pollRef.current); };
  }, [fetchOnce]);

  const reanalyze = async (answers) => {
    try {
      await api.post(`/decisions/${id}/reanalyze`, { answers });
      setDecision({ ...decision, status: "analyzing" });
      const tick = async () => {
        const d = await fetchOnce();
        if (d && d.status === "analyzing") pollRef.current = setTimeout(tick, 3000);
      };
      tick();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/decisions/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `decision-brief-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export PDF"); } finally { setExporting(false); }
  };

  if (notFound) return <AppShell><div className="text-center py-20 text-slate-500">Decision not found. <button className="underline" onClick={() => navigate("/dashboard")}>Back to dashboard</button></div></AppShell>;
  if (!decision) return <AppShell><div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></AppShell>;

  if (decision.status === "analyzing") return <AppShell><Analyzing decisionText={decision.decision_text} /></AppShell>;

  if (decision.status === "error") {
    return (
      <AppShell>
        <div className="max-w-lg mx-auto text-center py-16">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-4" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">Analysis failed</h2>
          <p className="text-slate-500 mb-6 text-sm">{decision.error || "Something went wrong while researching."}</p>
          <Button onClick={() => reanalyze({})} className="bg-slate-900 hover:bg-slate-800 rounded-lg gap-2"><RefreshCw className="w-4 h-4" /> Retry</Button>
        </div>
      </AppShell>
    );
  }

  const a = decision.analysis || {};
  const dash = a.decision_dashboard || {};
  const diff = a.your_differences || {};
  const patterns = a.outcome_patterns || {};
  const cases = (a.cases || []).slice().sort((x, y) => (y.similarity?.overall || 0) - (x.similarity?.overall || 0));

  return (
    <AppShell>
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-600">{a.decision_summary?.decision_type || "Decision"}</span>
            {decision.is_demo && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">DEMO DATA</span>}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight" data-testid="report-title">{decision.decision_text}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StrengthBadge value={a.evidence_strength} label="evidence" testid="evidence-strength-badge" />
          <StrengthBadge value={a.confidence} label="confidence" testid="confidence-badge" />
          <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-button" className="rounded-lg gap-1.5 h-8">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF brief
          </Button>
        </div>
      </div>

      {a.insufficient_evidence && (
        <Card className="p-5 mb-6 border-amber-200 bg-amber-50" testid="insufficient-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">We could not find enough reliable comparable cases to make a strong historical comparison. Treat the analysis below as low-confidence and answer the follow-up questions to improve it.</p>
          </div>
        </Card>
      )}

      {a.follow_up_questions?.length > 0 && <div className="mb-6"><FollowUp questions={a.follow_up_questions} onSubmit={reanalyze} /></div>}

      {/* Executive dashboard */}
      <Card className="p-6 sm:p-8 mb-6" testid="decision-dashboard">
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 mb-6">Should you consider this decision?</h2>
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <div className="border border-slate-200 rounded-lg p-4">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Historical evidence</span>
            <div className="mt-2"><StrengthBadge value={dash.historical_evidence} /></div>
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Comparable cases</span>
            <p className="font-mono text-sm mt-2 text-slate-800">
              <span className="text-emerald-600 font-semibold">{a.outcome_breakdown?.positive || 0}+</span> ·{" "}
              <span className="text-rose-600 font-semibold">{a.outcome_breakdown?.negative || 0}−</span> ·{" "}
              <span className="text-slate-500 font-semibold">{(a.outcome_breakdown?.mixed || 0) + (a.outcome_breakdown?.unclear || 0)}?</span>
              <span className="text-slate-400"> of {a.comparable_cases_count || cases.length}</span>
            </p>
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Your similarity</span>
            <div className="mt-2"><StrengthBadge value={dash.your_similarity} /></div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
          <Insight icon={TrendingUp} tone="pos" title="Biggest potential upside" text={dash.biggest_upside} />
          <Insight icon={TrendingDown} tone="neg" title="Biggest risk" text={dash.biggest_risk} />
          <Insight icon={HelpCircle} tone="info" title="Biggest unknown" text={dash.biggest_unknown} />
          <Insight icon={GitCompareArrows} tone="info" title="Most important difference" text={dash.most_important_difference} />
        </div>
        {dash.suggested_next_step && (
          <div className="mt-6 pt-6 border-t border-slate-100 flex items-start gap-3 bg-slate-50 -mx-6 sm:-mx-8 -mb-6 sm:-mb-8 p-6 sm:p-8 rounded-b-xl">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center shrink-0"><Lightbulb className="w-4.5 h-4.5 text-white" /></div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">Suggested next step</span>
              <p className="text-sm text-slate-800 font-medium mt-1 leading-relaxed" data-testid="next-step">{dash.suggested_next_step}</p>
            </div>
          </div>
        )}
      </Card>

      {/* Decision summary */}
      <Card className="p-6 mb-6" testid="decision-summary">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">What we understood</h3>
        <SummaryGrid s={a.decision_summary || {}} />
      </Card>

      {/* Cases */}
      <div className="flex items-center justify-between mb-4 mt-10">
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Comparable cases found: <span className="font-mono">{a.comparable_cases_count || cases.length}</span></h2>
      </div>
      <Card className="p-6 mb-6">
        <OutcomeBar b={a.outcome_breakdown || {}} />
      </Card>

      {cases.length > 0 && (
        <Card className="p-6 mb-6" testid="similarity-chart-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><BarChart3 className="w-4.5 h-4.5 text-slate-400" /> Case similarity, colored by outcome</h3>
          <p className="text-xs text-slate-500 mb-4">Higher = closer to your business. Green positive · red negative · amber mixed · grey unclear.</p>
          <CaseSimilarityChart cases={cases} />
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mb-10">
        {cases.map((c, i) => <CaseCard key={c.id || i} c={c} index={i} onOpen={setOpenCase} />)}
        {cases.length === 0 && <p className="text-slate-400 text-sm col-span-full text-center py-8">No comparable cases available.</p>}
      </div>

      {/* Patterns */}
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <Card className="p-6" testid="patterns-success">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><CheckCircle2 className="w-4.5 h-4.5 text-emerald-600" /> Successful cases commonly had</h3>
          <List items={patterns.successful_common} icon={ChevronRight} tone="pos" />
        </Card>
        <Card className="p-6" testid="patterns-fail">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><AlertTriangle className="w-4.5 h-4.5 text-rose-600" /> Unsuccessful cases commonly had</h3>
          <List items={patterns.unsuccessful_common} icon={ChevronRight} tone="neg" />
        </Card>
      </div>

      {/* Your differences */}
      <Card className="p-6 sm:p-8 mb-6" testid="your-differences">
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 mb-6">What makes your business different?</h2>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <Block title="You are similar to successful cases because" items={diff.similar_to_successful} icon={CheckCircle2} tone="pos" />
          <Block title="You are different because" items={diff.different_because} icon={GitCompareArrows} tone="info" />
          <Block title="Important risks" items={diff.important_risks} icon={TrendingDown} tone="neg" />
          <Block title="Important opportunities" items={diff.important_opportunities} icon={TrendingUp} tone="pos" />
        </div>
      </Card>

      {/* Unknowns */}
      {a.unknowns?.length > 0 && (
        <Card className="p-6 mb-6" testid="unknowns-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Info className="w-4.5 h-4.5 text-slate-400" /> Unknowns that could change this conclusion</h3>
          <List items={a.unknowns} icon={ChevronRight} tone="info" />
        </Card>
      )}

      <div className="mb-6"><ImplementBlock decision={decision} analysis={a} onSaved={setDecision} /></div>

      {a.web_sources?.length > 0 && (
        <Card className="p-6 mb-6" testid="web-sources-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><ExternalLink className="w-4.5 h-4.5 text-slate-400" /> Sources consulted</h3>
          <p className="text-xs text-slate-500 mb-4">Real public pages our research read while building this brief.</p>
          <ul className="space-y-2">
            {a.web_sources.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="font-mono text-[10px] text-slate-400 mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <a href={s.url} target="_blank" rel="noreferrer" data-testid={`web-source-${i}`}
                  className="text-blue-700 hover:underline flex items-center gap-1 break-all">{s.title} <ExternalLink className="w-3 h-3 shrink-0" /></a>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <p className="text-xs text-slate-400 text-center font-mono max-w-xl mx-auto mb-4">
        These comparable businesses experienced these outcomes. This is not a prediction — your situation is similar and different in the specific ways shown above.
      </p>

      <CaseDetailDialog open={!!openCase} c={openCase} onClose={() => setOpenCase(null)} />
    </AppShell>
  );
}

function Insight({ icon: Icon, tone, title, text }) {
  const toneCls = { pos: "text-emerald-600", neg: "text-rose-600", info: "text-slate-400" }[tone];
  return (
    <div className="flex items-start gap-3">
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${toneCls}`} />
      <div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{title}</span>
        <p className="text-sm text-slate-800 mt-0.5 leading-relaxed">{text || "—"}</p>
      </div>
    </div>
  );
}

function Block({ title, items, icon, tone }) {
  return (
    <div>
      <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">{title}</h4>
      <List items={items} icon={icon} tone={tone} />
    </div>
  );
}
